(() => {
  const KEY = 'meroo_wishlist';
  const valid = (items) => Array.isArray(items)
    ? [...new Set(items.filter((item) => typeof item === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(item)))] : [];
  class MerooWishlist {
    constructor() {
      this.items = this.read();
      this.generation = 0;
      document.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-wishlist-handle]');
        if (button) {
          event.preventDefault();
          const handle = button.dataset.wishlistHandle;
          if (!valid([handle]).length) return;
          this.items = this.read();
          const removing = this.items.includes(handle);
          const pageCard = button.closest('[data-wishlist-card]');
          const focusIndex = pageCard ? [...pageCard.parentElement.children].indexOf(pageCard) : undefined;
          this.items = removing ? this.items.filter((item) => item !== handle) : [...this.items, handle];
          this.save();
          this.sync();
          this.announce(this.storageFailed ? 'Browser storage is unavailable. Your wishlist may not be saved after leaving this page.' : removing ? 'Removed from your wishlist.' : 'Saved to your wishlist.');
          this.render(focusIndex);
        }
        if (event.target.closest('[data-wishlist-retry]')) this.render();
      });
      document.addEventListener('change', (event) => {
        if (!event.target.matches('[data-wishlist-variant]')) return;
        const card = event.target.closest('[data-wishlist-product]');
        card.querySelector('[data-wishlist-price]').textContent = event.target.selectedOptions[0].dataset.price;
        card.querySelector('[name="quantity"]').value = event.target.selectedOptions[0].dataset.quantity;
      });
      window.addEventListener('storage', (event) => {
        if (event.key !== KEY && event.key !== null) return;
        this.items = this.read();
        this.sync();
        this.render();
      });
      // Filters and recommendations replace cards without a page reload.
      new MutationObserver((changes) => {
        if (changes.some((change) => [...change.addedNodes].some((node) => node.nodeType === 1 &&
          (node.matches('[data-wishlist-handle]') || node.querySelector('[data-wishlist-handle]'))))) this.sync();
      }).observe(document.body, { childList: true, subtree: true });
      this.sync();
      this.render();
    }
    read() {
      if (this.storageFailed) return this.items || [];
      try { return valid(JSON.parse(localStorage.getItem(KEY) || '[]')); }
      catch { return this.items || []; }
    }
    save() {
      try {
        localStorage.setItem(KEY, JSON.stringify(this.items));
        this.storageFailed = false;
      } catch { this.storageFailed = true; }
    }
    announce(message) {
      let status = document.getElementById('WishlistStatus');
      if (!status) {
        status = document.createElement('p');
        status.id = 'WishlistStatus';
        status.className = 'meroo-wishlist-toast';
        status.setAttribute('role', 'status');
        document.body.append(status);
      }
      status.textContent = message;
      status.hidden = false;
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => { status.hidden = true; }, 5000);
    }
    sync() {
      document.querySelectorAll('[data-wishlist-handle]').forEach((button) => {
        const active = this.items.includes(button.dataset.wishlistHandle);
        const productBtn = button.classList.contains('meroo-wishlist-btn--product');
        const label = active
          ? productBtn
            ? 'Remove'
            : 'Remove from wishlist'
          : productBtn
            ? 'Add to wishlist'
            : 'Add to wishlist';
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
        const text = button.querySelector('[data-wishlist-label]');
        if (text) text.textContent = label;
      });
      document.querySelectorAll('#WishlistBadge').forEach((badge) => { badge.textContent = this.items.length || ''; });
      document.querySelectorAll('[data-mcp-wishlist-count]').forEach((badge) => {
        badge.textContent = String(this.items.length);
        badge.hidden = this.items.length === 0;
      });
      const count = document.getElementById('WishlistPageCount');
      if (count) count.textContent = `${this.items.length} ${this.items.length === 1 ? 'item' : 'items'}`;
    }
    async loadCard(handle, signal) {
      const root = window.routes?.root_url || '/';
      const url = new URL(`${root.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}`, location.origin);
      url.searchParams.set('view', 'wishlist-card');
      // Keep explicit theme-preview links on the same theme for fragment requests.
      const previewTheme = new URLSearchParams(location.search).get('preview_theme_id');
      if (previewTheme && /^\d+$/.test(previewTheme)) url.searchParams.set('preview_theme_id', previewTheme);
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (signal.aborted) throw new DOMException('Wishlist changed.', 'AbortError');
        const controller = new AbortController();
        const cancel = () => controller.abort();
        signal.addEventListener('abort', cancel, { once: true });
        const timeout = setTimeout(cancel, 8000);
        try {
          const response = await fetch(url.href, {
            credentials: 'same-origin',
            signal: controller.signal,
            cache: attempt ? 'reload' : 'default',
          });
          if (!response.ok) {
            const error = new Error(response.status === 404 || response.status === 410 ? 'unavailable' : 'connection');
            error.retryable = response.status === 408 || response.status === 429 || response.status >= 500;
            throw error;
          }
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const card = parsed.querySelector('[data-wishlist-product]');
          if (!card || card.dataset.wishlistProduct !== handle) throw new Error('connection');
          return card;
        } catch (error) {
          if (signal.aborted) throw new DOMException('Wishlist changed.', 'AbortError');
          if (error.retryable === false || attempt === 2) throw error;
        } finally {
          clearTimeout(timeout);
          signal.removeEventListener('abort', cancel);
        }
        await new Promise((resolve, reject) => {
          const cancelWait = () => {
            clearTimeout(timer);
            reject(new DOMException('Wishlist changed.', 'AbortError'));
          };
          const timer = setTimeout(() => {
            signal.removeEventListener('abort', cancelWait);
            resolve();
          }, 350 * (attempt + 1));
          signal.addEventListener('abort', cancelWait, { once: true });
          if (signal.aborted) cancelWait();
        });
      }
    }
    async render(focusIndex) {
      const content = document.getElementById('WishlistContent');
      if (!content) return;
      this.renderController?.abort();
      this.renderController = new AbortController();
      const signal = this.renderController.signal;
      const generation = ++this.generation;
      const handles = [...this.items];
      content.replaceChildren();
      content.hidden = !handles.length;
      const empty = document.getElementById('WishlistEmpty');
      if (empty) empty.hidden = !!handles.length;
      if (!handles.length && focusIndex !== undefined) {
        document.querySelector('#WishlistEmpty a')?.focus();
      }
      content.setAttribute('aria-busy', String(!!handles.length));
      const slots = handles.map((handle) => {
        const slot = document.createElement('div');
        slot.dataset.wishlistCard = handle;
        slot.className = 'meroo-skeleton-card';
        content.append(slot);
        return slot;
      });
      for (let offset = 0; offset < handles.length; offset += 4) {
        if (generation !== this.generation) return;
        await Promise.all(handles.slice(offset, offset + 4).map(async (handle, index) => {
          const slot = slots[offset + index];
          try {
            const card = await this.loadCard(handle, signal);
            if (generation !== this.generation) return;
            slot.replaceChildren(document.importNode(card, true));
          } catch (error) {
            if (generation !== this.generation) return;
            // Network failures must never erase saved products.
            const message = document.createElement('p');
            message.textContent = error.message === 'unavailable' ? 'This product is no longer available.' : 'This saved product could not load. Please try again.';
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.dataset.wishlistHandle = handle;
            remove.textContent = 'Remove from wishlist';
            if (error.message === 'unavailable') {
              slot.replaceChildren(message, remove);
            } else {
              const retry = document.createElement('button');
              retry.type = 'button';
              retry.dataset.wishlistRetry = '';
              retry.textContent = 'Try again';
              const view = document.createElement('a');
              view.className = 'meroo-wishlist__text-link';
              view.href = `${(window.routes?.root_url || '/').replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}`;
              view.textContent = 'View saved shade';
              slot.replaceChildren(message, retry, view, remove);
            }
          } finally { slot.classList.remove('meroo-skeleton-card'); }
        }));
      }
      if (generation === this.generation) {
        content.setAttribute('aria-busy', 'false');
        this.sync();
        if (handles.length && focusIndex !== undefined && document.activeElement === document.body) {
          const nextSlot = slots[Math.min(focusIndex, slots.length - 1)];
          nextSlot.querySelector('[data-wishlist-handle]')?.focus();
        }
      }
    }
  }
  const start = () => { if (!window.merooWishlist) window.merooWishlist = new MerooWishlist(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
